import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { Linkedin, ArrowUpRight, Mail, Phone, MapPin } from 'lucide-react';
import { getPaths } from '../../utils/paths';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_54396c88-8cf9-4315-926b-d20a7ab432d7/artifacts/hhkc1a2d_kesult_logo_webp.webp";

export const Footer = () => {
  const { language, t } = useLanguage();
  const basePath = language === 'en' ? '/en' : '';

  const quickLinks = [
    { href: language === 'en' ? '/en/how-i-work' : '/werkwijze', label: t('nav.werkwijze') },
    { href: language === 'en' ? '/en/seo-packages' : '/seo-pakketten', label: language === 'nl' ? 'SEO Pakketten' : 'SEO Packages' },
    { href: `${basePath}/seo-consultant`, label: language === 'nl' ? 'SEO specialist inhuren' : 'Hire Dutch SEO Specialist' },
    { href: language === 'en' ? '/en/about' : '/over-mij', label: t('nav.overMij') },
    { href: `${basePath}/contact`, label: t('nav.contact') },
    { href: `${basePath}/blog`, label: 'Blog' },
  ];

  const paths = getPaths(language);

  const services = [
    { href: paths.service('seo-strategie'), label: language === 'nl' ? 'SEO Strategie' : 'SEO Strategy' },
    { href: paths.service('technische-seo'), label: language === 'nl' ? 'Technische SEO' : 'Technical SEO' },
    { href: paths.service('seo-tekstschrijver'), label: language === 'nl' ? 'SEO Teksten' : 'SEO Copywriting' },
    { href: paths.service('linkbuilding'), label: language === 'nl' ? 'Linkbuilding' : 'Link Building' },
    { href: paths.service('local-seo'), label: language === 'nl' ? 'Lokale SEO' : 'Local SEO' },
    { href: paths.service('ecommerce-seo'), label: 'Ecommerce SEO' },
  ];

  return (
    <footer className="bg-[#08080A] text-white" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">

          {/* Column 1: Brand */}
          <div>
            <Link to={`${basePath}/`} className="inline-block mb-5">
              <img src={LOGO_URL} alt="Kesult" className="h-8 w-auto brightness-0 invert" />
            </Link>
            <p className="text-gray-400 text-sm leading-relaxed mb-6">
              {language === 'nl'
                ? 'Freelance SEO specialist gespecialiseerd in holistische SEO-strategieën voor ambitieuze bedrijven in Nederland.'
                : 'Your local SEO partner for the Dutch market. Helping international businesses gain organic visibility in the Netherlands.'}
            </p>
            <a
              href="https://www.linkedin.com/in/falco-keiman/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-gray-400 hover:text-[#4286F4] transition-colors"
              data-testid="footer-linkedin"
            >
              <div className="w-9 h-9 bg-white/10 rounded-lg flex items-center justify-center">
                <Linkedin className="h-4 w-4" />
              </div>
            </a>
          </div>

          {/* Column 2: Quick Links */}
          <div>
            <h4 className="text-sm font-bold uppercase tracking-[0.2em] text-[#4286F4] mb-6">
              {t('footer.quickLinks')}
            </h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-gray-400 hover:text-white transition-colors text-sm flex items-center gap-1.5 group"
                  >
                    {link.label}
                    <ArrowUpRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3: Services */}
          <div>
            <h4 className="text-sm font-bold uppercase tracking-[0.2em] text-[#4286F4] mb-6">
              {t('nav.diensten')}
            </h4>
            <ul className="space-y-3">
              {services.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-gray-400 hover:text-white transition-colors text-sm flex items-center gap-1.5 group"
                  >
                    {link.label}
                    <ArrowUpRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 4: Contact */}
          <div>
            <h4 className="text-sm font-bold uppercase tracking-[0.2em] text-[#4286F4] mb-6">
              Contact
            </h4>
            <ul className="space-y-4">
              <li>
                <a href="mailto:info@kesult.com" className="flex items-start gap-3 text-gray-400 hover:text-white transition-colors text-sm">
                  <Mail className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  info@kesult.com
                </a>
              </li>
              <li>
                <a href="tel:+31612345678" className="flex items-start gap-3 text-gray-400 hover:text-white transition-colors text-sm">
                  <Phone className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  +31 6 12 34 56 78
                </a>
              </li>
              <li className="flex items-start gap-3 text-gray-400 text-sm">
                <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>Veenendaal, Nederland</span>
              </li>
            </ul>
            <div className="mt-6 pt-5 border-t border-white/10">
              <p className="text-gray-500 text-xs">KVK: 42002486</p>
            </div>
          </div>

        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 mt-16 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-gray-500 text-sm">
            {t('footer.copyright')}
          </p>
          <div className="flex items-center gap-6 text-sm text-gray-500">
            <Link to={language === 'en' ? '/en/disclaimer' : '/disclaimer'} className="hover:text-white transition-colors">
              Disclaimer
            </Link>
            <Link to={language === 'en' ? '/en/privacy-policy' : '/privacyverklaring'} className="hover:text-white transition-colors">
              {language === 'nl' ? 'Privacyverklaring' : 'Privacy Policy'}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
